import usePrevious from '@restart/hooks/usePrevious';
import React, { useCallback, useContext, useEffect, useRef } from 'react';

const eventName = propName => propName.slice(2).toLowerCase();

export const EventContext = React.createContext(null);

function useUpdatedRef(value) {
  const valueRef = useRef(value);
  valueRef.current = value;
  return valueRef;
}
/**
 * Listens for events on a parent IntractiveGroup. Helpful for locating
 * event handlers to on a component where you need to listen for broader events
 * such as mousemove.
 */


export default function useEventTarget(props) {
  const hasSet = useRef(false);
  const {
    current: handlers
  } = useRef(new Map());
  const delegateTarget = useContext(EventContext);
  const lastTarget = usePrevious(delegateTarget);
  const teardown = useCallback(target => {
    for (const [key, value] of handlers.entries()) {
      target.off(eventName(key), value);
    }

    handlers.clear();
  }, [handlers]); // we need a ref so that handlers can have a stable identify and we can avoid
  // binding and unbinding on each render

  const propsRef = useUpdatedRef(props || {});

  if (lastTarget && lastTarget !== delegateTarget) {
    hasSet.current = false;
    teardown(lastTarget);
  }

  function updateListeners(target) {
    if (!props) return;

    for (const [key, value] of handlers.entries()) {
      if (!props[key]) {
        handlers.delete(key);
        target.off(eventName(key), value);
      }
    }

    for (const key of Object.keys(props)) {
      if (handlers.has(key)) continue;

      const handler = e => {
        propsRef.current[key](e);
      };

      handlers.set(key, handler);
      target.on(eventName(key), handler);
    }

    hasSet.current = true;
  }

  if (delegateTarget) {
    updateListeners(delegateTarget);
  } // only need to update in an effect if the ref isn't resolved yet


  useEffect(() => {
    if (delegateTarget && !hasSet.current) {
      updateListeners(delegateTarget);
    }
  });
  useEffect(() => {
    return () => {
      if (delegateTarget) teardown(delegateTarget);
    };
  }, [delegateTarget, teardown]);
}